import { IActivityLogsResponse, IDashboardStatisticsResponse, IGetConflictResolutionPerformanceResponse, IGetContributionPerformanceResponse, IGetLastActivitiesResponse, IGetProcessedResourcesResponse, IGetSpecificationItemSourcesResponse } from "@/shared/services/interface/responses/dashboard";
import { createSlice } from "@reduxjs/toolkit";
import { getDashboardStatistics, getActivityLogs, getSpecificationItemSources, getLastActivities, getProcessedResources, getContributionPerformance, getConflictResolutionPerformance } from "../thunks/dashboardThunk";
import { DateRange } from "react-day-picker";

export const SCREEN_NAME_TYPES = ['specification_create', 'suppliers', 'compare', 'explore', 'partners']

interface IDashboardState {
  statistics: IDashboardStatisticsResponse | null;
  loadingStatistics: boolean;
  errorStatistics: string | null;
  activityLogs: IActivityLogsResponse["activityLogs"] | null;
  totalDuration: number | null;
  loadingActivityLogs: boolean;
  errorActivityLogs: string | null;
  specificationItemSources: IGetSpecificationItemSourcesResponse | null;
  loadingSpecificationItemSources: boolean;
  errorSpecificationItemSources: string | null;
  headerFilters: {
    dateRange: DateRange | Date | undefined;
    selectedProjectId: string;
    itemType: "folder" | "specification" | "all";
  }
  filterParams: {
    folderId: string | undefined;
    specificationId: string | undefined;
    startDate: string | undefined;
    endDate: string | undefined;
  },
  lastActivities: IGetLastActivitiesResponse | null;
  loadingLastActivities: boolean;
  errorLastActivities: string | null;
  processedResources: IGetProcessedResourcesResponse | null;
  loadingProcessedResources: boolean;
  errorProcessedResources: string | null;
  contributionPerformance: IGetContributionPerformanceResponse | null;
  loadingContributionPerformance: boolean;
  errorContributionPerformance: string | null;
  conflictResolutionPerformance: IGetConflictResolutionPerformanceResponse | null;
  loadingConflictResolutionPerformance: boolean;
  errorConflictResolutionPerformance: string | null;
}

const initialState: IDashboardState = {
  statistics: null,
  loadingStatistics: false,
  errorStatistics: null,
  activityLogs: null,
  totalDuration: null,
  loadingActivityLogs: false,
  errorActivityLogs: null,
  specificationItemSources: null,
  loadingSpecificationItemSources: false,
  errorSpecificationItemSources: null,
  headerFilters: {
    dateRange: undefined,
    selectedProjectId: "all",
    itemType: "all",
  },
  filterParams: {
    folderId: undefined,
    specificationId: undefined,
    startDate: undefined,
    endDate: undefined,
  },
  lastActivities: null,
  loadingLastActivities: false,
  errorLastActivities: null,
  processedResources: null,
  loadingProcessedResources: false,
  errorProcessedResources: null,
  contributionPerformance: null,
  loadingContributionPerformance: false,
  errorContributionPerformance: null,
  conflictResolutionPerformance: {
    aiSuccessPercentage: 0,
    totalSpecificationItem: 0,
    totalCitationCount: 0,
    totalSupplierCount: 0,
    supplierSolvedItemCount: 0,
    overlappedItemCount: 0,
    totalSupplierTimeSpent: 0,
  },
  loadingConflictResolutionPerformance: false,
  errorConflictResolutionPerformance: null,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setStatistics: (state, action) => {
      state.statistics = action.payload;
    },
    setLoadingStatistics: (state, action) => {
      state.loadingStatistics = action.payload;
    },
    setErrorStatistics: (state, action) => {
      state.errorStatistics = action.payload;
    },
    setActivityLogs: (state, action) => {
      state.activityLogs = action.payload;
    },
    setLoadingActivityLogs: (state, action) => {
      state.loadingActivityLogs = action.payload;
    },
    setErrorActivityLogs: (state, action) => {
      state.errorActivityLogs = action.payload;
    },
    setSpecificationItemSources: (state, action) => {
      state.specificationItemSources = action.payload;
    },
    setLoadingSpecificationItemSources: (state, action) => {
      state.loadingSpecificationItemSources = action.payload;
    },
    setErrorSpecificationItemSources: (state, action) => {
      state.errorSpecificationItemSources = action.payload;
    },
    setHeaderFilters: (state, action) => {
      const headerFilters = action.payload;
      state.headerFilters = headerFilters;
      let specificationId = undefined;
      let folderId = undefined;
      let startDate = undefined;
      let endDate = undefined;
      if (headerFilters.itemType === "specification") {
        specificationId = headerFilters.selectedProjectId;
      } else if (headerFilters.itemType === "folder") {
        folderId = headerFilters.selectedProjectId;
      }
      if (headerFilters.dateRange && typeof headerFilters.dateRange === "object" && "from" in headerFilters.dateRange) {
        startDate = headerFilters.dateRange.from?.toISOString();
        endDate = headerFilters.dateRange.to?.toISOString();
      }
      state.filterParams = {
        folderId,
        specificationId,
        startDate,
        endDate,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDashboardStatistics.pending, (state) => {
        state.loadingStatistics = true;
        state.errorStatistics = null;
      })
      .addCase(getDashboardStatistics.fulfilled, (state, action) => {
        state.loadingStatistics = false;
        state.statistics = action.payload;
        state.errorStatistics = null;
      })
      .addCase(getDashboardStatistics.rejected, (state, action) => {
        state.loadingStatistics = false;
        state.errorStatistics = action.payload as string;
      })
      .addCase(getActivityLogs.pending, (state) => {
        state.loadingActivityLogs = true;
        state.errorActivityLogs = null;
      })
      .addCase(getActivityLogs.fulfilled, (state, action) => {
        state.loadingActivityLogs = false;
        let activityLogs = action.payload.activityLogs;

        activityLogs = SCREEN_NAME_TYPES.map(
          (screenName: string) => {
            return activityLogs.find((log: IActivityLogsResponse["activityLogs"][0]) => log.screenName === screenName) ?? { screenName: screenName, totalDuration: 0, percentage: 0 };
          }
        );
        state.activityLogs = activityLogs;
        state.totalDuration = action.payload.totalDuration;
        state.errorActivityLogs = null;
      })
      .addCase(getActivityLogs.rejected, (state, action) => {
        state.loadingActivityLogs = false;
        state.errorActivityLogs = action.payload as string;
      })
      .addCase(getSpecificationItemSources.pending, (state) => {
        state.loadingSpecificationItemSources = true;
        state.errorSpecificationItemSources = null;
      })
      .addCase(getSpecificationItemSources.fulfilled, (state, action) => {
        state.loadingSpecificationItemSources = false;
        state.specificationItemSources = action.payload;
        state.errorSpecificationItemSources = null;
      })
      .addCase(getSpecificationItemSources.rejected, (state, action) => {
        state.loadingSpecificationItemSources = false;
        state.errorSpecificationItemSources = action.payload as string;
      })
      .addCase(getLastActivities.pending, (state) => {
        state.loadingLastActivities = true;
        state.errorLastActivities = null;
      })
      .addCase(getLastActivities.fulfilled, (state, action) => {
        state.loadingLastActivities = false;
        // remove activities without specification
        const lastActivities = action.payload.flatMap((activity) => {
          const { activities = [] } = activity;
          const activitiesFiltered = activities.filter(({ metadata }) => {
            return metadata?.specification?.name
          });
          const activitiesWithSpecExists = activitiesFiltered.length > 0
          if (activitiesWithSpecExists) {
            return {
              ...activity,
              activities: activitiesFiltered,
            }
          }
          return [];
        });
        state.lastActivities = lastActivities.filter((activity) => activity);
        state.errorLastActivities = null;
      })
      .addCase(getLastActivities.rejected, (state, action) => {
        state.loadingLastActivities = false;
        state.errorLastActivities = action.payload as string;
      })
      .addCase(getProcessedResources.pending, (state) => {
        state.loadingProcessedResources = true;
        state.errorProcessedResources = null;
      })
      .addCase(getProcessedResources.fulfilled, (state, action) => {
        state.loadingProcessedResources = false;
        state.processedResources = action.payload;
        state.errorProcessedResources = null;
      })
      .addCase(getProcessedResources.rejected, (state, action) => {
        state.loadingProcessedResources = false;
        state.errorProcessedResources = action.payload as string;
      })
      .addCase(getContributionPerformance.pending, (state) => {
        state.loadingContributionPerformance = true;
        state.errorContributionPerformance = null;
      })
      .addCase(getContributionPerformance.fulfilled, (state, action) => {
        state.loadingContributionPerformance = false;
        state.contributionPerformance = action.payload;
        state.errorContributionPerformance = null;
      })
      .addCase(getContributionPerformance.rejected, (state, action) => {
        state.loadingContributionPerformance = false;
        state.errorContributionPerformance = action.payload as string;
      })
      .addCase(getConflictResolutionPerformance.pending, (state) => {
        state.loadingConflictResolutionPerformance = true;
        state.errorConflictResolutionPerformance = null;
      })
      .addCase(getConflictResolutionPerformance.fulfilled, (state, action) => {
        state.loadingConflictResolutionPerformance = false;
        state.conflictResolutionPerformance = action.payload;
        state.errorConflictResolutionPerformance = null;
      })
      .addCase(getConflictResolutionPerformance.rejected, (state, action) => {
        state.loadingConflictResolutionPerformance = false;
        state.errorConflictResolutionPerformance = action.payload as string;
      })
  },
});

export default dashboardSlice.reducer;
export const { setStatistics, setLoadingStatistics, setErrorStatistics, setActivityLogs, setLoadingActivityLogs, setErrorActivityLogs, setSpecificationItemSources, setLoadingSpecificationItemSources, setErrorSpecificationItemSources, setHeaderFilters } =
  dashboardSlice.actions;
